import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { updateRefreshToken, upsertSavedAccount } from '@/lib/saved-accounts';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Clear local session only — does NOT call server logout, so the refresh token stays valid server-side. Use for account switching / quick-login scenarios. */
  signOutLocally: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  signOutLocally: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const persistAccountFromSession = (nextSession: Session | null) => {
    if (!nextSession?.user || !nextSession.refresh_token) return;

    const metadata = nextSession.user.user_metadata || {};
    // Fire-and-forget async encryption
    upsertSavedAccount({
      id: nextSession.user.id,
      email: nextSession.user.email || undefined,
      phone: nextSession.user.phone || undefined,
      provider: nextSession.user.app_metadata?.provider || 'email',
      display_name: metadata.full_name || metadata.name || nextSession.user.email || nextSession.user.phone || '用户',
      avatar_url: metadata.avatar_url || null,
      refresh_token: nextSession.refresh_token,
    }).catch(console.error);
  };

  useEffect(() => {
    let latestTokenSaved = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      // Only clear loading on events that definitively establish auth state.
      // INITIAL_SESSION with null may fire before the OAuth callback has
      // stored the session — wait for getSession() below to confirm.
      if (event !== 'INITIAL_SESSION' || nextSession) {
        setLoading(false);
      }

      // Sync Sentry user context
      if (nextSession?.user) {
        setSentryUser(nextSession.user.id, nextSession.user.email);
      } else {
        clearSentryUser();
      }

      if (nextSession?.user && nextSession.refresh_token) {
        if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          updateRefreshToken(nextSession.user.id, nextSession.refresh_token).catch(console.error);
          persistAccountFromSession(nextSession);
          latestTokenSaved = true;
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession((prev) => prev ?? currentSession);
      setUser((prev) => prev ?? currentSession?.user ?? null);
      setLoading(false);
      if (!latestTokenSaved && currentSession) {
        persistAccountFromSession(currentSession);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (currentSession?.user && currentSession.refresh_token) {
      await updateRefreshToken(currentSession.user.id, currentSession.refresh_token);
      persistAccountFromSession(currentSession);
    }
    await supabase.auth.signOut({ scope: 'local' });
  };

  const signOutLocally = async () => {
    const storageKey = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID || 'jbjupuzwhntcjrqqtybc'}-auth-token`;
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.user?.id && parsed?.refresh_token) {
          // Await token persistence to avoid race conditions
          await updateRefreshToken(parsed.user.id, parsed.refresh_token);
          await upsertSavedAccount({
            id: parsed.user.id,
            email: parsed.user.email || undefined,
            phone: parsed.user.phone || undefined,
            provider: parsed.user.app_metadata?.provider || 'email',
            display_name: parsed.user.user_metadata?.full_name || parsed.user.user_metadata?.name || parsed.user.email || parsed.user.phone || '用户',
            avatar_url: parsed.user.user_metadata?.avatar_url || null,
            refresh_token: parsed.refresh_token,
          });
        }
      } catch (e) {
        console.error('Failed to persist account before sign out:', e);
      }
    }
    localStorage.removeItem(storageKey);
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signOut, signOutLocally }}>
      {children}
    </AuthContext.Provider>
  );
};
